<template>
  <div class="actions-toolbar mb-4 sticky top-0 z-9">
    <div class="actions-toolbar__nav rounded-t-2 pa-4 pb-4 flex justify-between items-center flex-wrap">
      <div
        class="breadcrumb-area flex gap-2 items-center"
        :class="launchDarkly.enable_multiple_lines_breadcrumb ? 'flex-1' : 'justify-between truncate'"
      >
        <NDropdown
          v-if="navigationOptions.length > 1"
          :on-select="(_, selectedOption) => onNavigationOptionSelect(selectedOption)"
          :options="navigationOptions"
          placement="bottom-start"
          :trigger="dropdownTriggerMethod"
        >
          <CButton class="p-2" icon-placement="left" :style="{ borderColor: themeVars.primary2 }">
            <template #icon>
              <CIcon class="" icon="ellipsis" size="16px" />
            </template>
          </CButton>
        </NDropdown>
        <NBreadcrumb
          v-if="selectedSection === 'all' && breadcrumbs.length"
          :class="
            launchDarkly.enable_multiple_lines_breadcrumb ? 'flex flex-wrap whitespace-normal' : 'overflow-x-auto'
          "
          separator=">"
        >
          <NBreadcrumbItem
            v-for="(b, idx) in breadcrumbs"
            :key="b.id"
            class="content__breadcrumb__item"
            :class="{ 'cursor-pointer': b.id !== apiState.currentFolderId }"
            :clickable="b.id !== apiState.currentFolderId"
            @click="onBreadcrumbClick(b)"
          >
            {{ !idx ? t('canvasUI.browserApp.content.breadcrumbs.root') : b.name }}
          </NBreadcrumbItem>
        </NBreadcrumb>
        <div v-else-if="selectedSection === 'favorites'" class="p-1 content__section-title">
          {{ t('canvasUI.browserApp.content.favorites') }}
        </div>
        <div v-else-if="selectedSection === 'recent'" class="p-1 content__section-title">
          {{ t('canvasUI.browserApp.content.recent') }}
        </div>
        <div v-else-if="selectedSection === 'trash'" class="p-1 content__section-title">
          {{ t('canvasUI.browserApp.content.trash') }}
        </div>
      </div>
      <div class="flex items-center gap-2 flex-shrink-0 ml-auto">
        <NDropdown
          v-if="selectedSection === 'all'"
          class="sort-dropdown"
          :on-select="(_, selectedOption) => onSortOptionSelect(selectedOption)"
          :options="sortOptions"
          placement="bottom-end"
          trigger="click"
          @update:show="onDropdownToggle"
        >
          <CButton class="text-sm font-medium flex items-center gap-1" size="small">
            {{ t('canvasUI.browserApp.content.sortBy') }}: {{ getCurrentSortLabel() }}
          </CButton>
        </NDropdown>
        <CButton
          v-if="launchDarkly.enable_smart_folders_in_files"
          v-tooltip="t('canvasUI.browserApp.content.smartFolders')"
          circle
          @click="enableSmartFolders"
        >
          <template #icon>
            <CIcon icon="folder-bookmark" />
          </template>
        </CButton>
        <CButton
          v-tooltip="t('canvasUI.browserApp.content.detailView')"
          circle
          :color="isContentPanelOpen ? themeVars.primary : undefined"
          @click="toggleContentPanel()"
        >
          <template #icon>
            <CIcon icon="info-circle" />
          </template>
        </CButton>
        <CButton v-tooltip="t('canvasUI.browserApp.content.refresh')" circle @click="onRefresh">
          <template #icon>
            <CIcon icon="arrow-rotate-right" />
          </template>
        </CButton>
        <NDropdown
          v-if="uploadOptions.length"
          :on-select="(_, selectedOption) => onNewDropdownSelect(selectedOption)"
          :options="uploadOptions"
          trigger="click"
        >
          <CButton icon-placement="left" type="primary">
            <template #icon>
              <CIcon icon="plus" size="16px" />
            </template>
            {{ t('canvasUI.browserApp.content.new') }}
          </CButton>
        </NDropdown>
      </div>
    </div>
    <SelectionActions v-if="showSelectionActions" class="h-[52px] pb-4 rounded-0 rounded-b-2" />
    <div v-else class="flex items-center px-4 pb-4 mh-13 gap-2 flex-wrap rounded-b-2 actions-toolbar__controls">
      <CSearchFilters
        ref="searchFiltersRef"
        dependent-filters-entity="file"
        :disabled="!!apiState.isFetchingListFiles"
        enable-dependent-filters
        entity="file"
        :filters="apiState.searchMetadataFilters"
        :hide-filters="selectedSection !== 'all'"
        :initial-search-value="apiState.searchQuery"
        :placeholder="t('canvasUI.canvasBuilder.sectionSelector.searchByNameOrTag')"
        v-bind="popularityProps"
        @update:filters="fetchFilesAfterFiltering"
        @update:search="handleSearch"
      >
        <template v-if="!disabledSearchContext" #input-suffix>
          <div
            v-if="selectedSection === 'all'"
            class="flex items-center justify-center min-h-[36px] pr-3 mr-1 border-r border-r-solid border-primary4"
          >
            <CButton
              class="rounded-r-0 border-r-0"
              size="tiny"
              :type="isLocalSearch ? 'primary' : 'default'"
              @click="() => toggleSearchMode(true)"
            >
              {{ t('canvasUI.browserApp.content.localSearch') }}
            </CButton>
            <CButton
              class="rounded-l-0"
              size="tiny"
              :type="isLocalSearch ? 'default' : 'primary'"
              @click="() => toggleSearchMode(false)"
            >
              {{ t('canvasUI.browserApp.content.globalSearch') }}
            </CButton>
          </div>
        </template>
      </CSearchFilters>
    </div>
  </div>
</template>

<script setup lang="ts">
import { NBreadcrumb, NBreadcrumbItem, NDropdown } from 'naive-ui'
import { useI18n } from 'vue-i18n'
import { type ComputedRef, computed, h, inject, onBeforeUnmount, ref, watch } from 'vue'
import type { MenuOption } from 'naive-ui/lib'

import type { AppCurrentState, SortOption } from '@browser/stores/app'
import useAppStore from '@browser/stores/app'
import useApiStore from '@browser/stores/api'
import type { MiniFolder } from '@openapi'
import useThemeVars from '@lib/composables/useThemeVars'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CSearchFilters from '@lib/components/CSearchFilters/CSearchFilters.vue'
import tooltipDirective from '@lib/directives/tooltip'
import SelectionActions from '@browser/components/Content/ActionsToolbar/SelectionActions.vue'
import useAppEmit from '@browser/composables/useAppEmit'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import useFileSmartFolderStore from '@browser/stores/fileSmartFolderStore'
import useFolderNameDescription from '@lib/composables/useFolderNameDescription'

const {
  toggleContentPanel,
  setCurrentState,
  selectedItemIds,
  selectedSection,
  appName,
  isContentPanelOpen,
  enabledFavorites,
  enabledRecents,
  isLocalSearch,
  setIsLocalSearch,
  disabledSearchContext,
  selectedSortOption,
  setSortOption,
} = useAppStore()
const { state: apiState, actions: apiActions } = useApiStore()
const { t } = useI18n()
const themeVars = useThemeVars()
const { appEmit } = useAppEmit()
const vTooltip = tooltipDirective
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const smartFolderStore = useFileSmartFolderStore()
const { parseNameOnly } = useFolderNameDescription()

const searchFiltersRef = ref<InstanceType<typeof CSearchFilters> | null>(null)
const isDropdownOpen = ref(false)

const showSelectionActions = computed(
  () => selectedItemIds.value.length && launchDarkly.value.allow_bulk_actions_canvases_files,
)

const breadcrumbs = computed(() => [
  ...apiState.currentFolderPath.map((folder) => ({
    ...folder,
    name: parseNameOnly(folder.name || ''),
  })),
  {
    id: apiState.currentFolderId,
    name: parseNameOnly(apiState.currentFolderName || ''),
  },
])

const dropdownTriggerMethod = window.matchMedia('(any-hover: none)').matches ? 'click' : 'hover'

const navigationOptions = computed(() => {
  const options = [
    {
      label: t('canvasUI.browserApp.sidebar.contentSelection.allContent'),
      value: 'all',
      icon: () => h(CIcon, { icon: 'folder' }),
    },
  ]
  if (enabledRecents.value) {
    options.push({
      label: t('canvasUI.browserApp.sidebar.contentSelection.recent'),
      value: 'recent',
      icon: () => h(CIcon, { icon: 'clock' }),
    })
  }
  if (enabledFavorites.value) {
    options.push({
      label: t('canvasUI.browserApp.sidebar.contentSelection.favorites'),
      value: 'favorites',
      icon: () => h(CIcon, { icon: 'heart' }),
    })
  }

  return options
})

const getSortOptionProps = (value: string) => ({
  style:
    selectedSortOption.value === value
      ? {
          backgroundColor: themeVars.value.primary5,
        }
      : {},
})

const sortOptions = computed(() => [
  {
    label: t('canvasUI.browserApp.content.sortOptions.nameAsc'),
    value: 'nameAsc',
    key: 'nameAsc',
    icon: () => h(CIcon, { icon: 'arrow-down-a-z', size: '16px' }),
    props: getSortOptionProps('nameAsc'),
  },
  {
    label: t('canvasUI.browserApp.content.sortOptions.nameDesc'),
    value: 'nameDesc',
    key: 'nameDesc',
    icon: () => h(CIcon, { icon: 'arrow-down-z-a', size: '16px' }),
    props: getSortOptionProps('nameDesc'),
  },
  {
    label: t('canvasUI.browserApp.content.sortOptions.modifiedAsc'),
    value: 'modifiedAsc',
    key: 'modifiedAsc',
    icon: () => h(CIcon, { icon: 'calendar-arrow-up', size: '16px' }),
    props: getSortOptionProps('modifiedAsc'),
  },
  {
    label: t('canvasUI.browserApp.content.sortOptions.modifiedDesc'),
    value: 'modifiedDesc',
    key: 'modifiedDesc',
    icon: () => h(CIcon, { icon: 'calendar-arrow-down', size: '16px' }),
    props: getSortOptionProps('modifiedDesc'),
  },
])

const uploadOptions = computed(() => [
  ...(appName.value === 'admin'
    ? [
        {
          label: t('canvasUI.browserApp.content.newFolder'),
          value: 'create-folder',
          icon: () =>
            h(CIcon, {
              icon: 'folder-plus',
            }),
        },
        {
          type: 'divider',
        },
      ]
    : []),
  ...(appName.value === 'admin' || (appName.value === 'impact' && launchDarkly.value?.allow_rep_file_upload)
    ? [
        {
          label: t('canvasUI.browserApp.content.fileUpload'),
          value: 'upload-files',
          icon: () =>
            h(CIcon, {
              icon: 'folder-arrow-up',
            }),
        },
      ]
    : []),
])

async function onBreadcrumbClick(item: MiniFolder) {
  if (item.id === apiState.currentFolderId) return
  await appEmit('onSectionChange', { section: 'all', folderId: item.id })
}

function onNavigationOptionSelect(selection: MenuOption) {
  appEmit('onSectionChange', { section: selection.value })
}

function getCurrentSortLabel() {
  const currentOption = sortOptions.value.find((option) => option.value === selectedSortOption.value)
  return currentOption?.label || sortOptions.value[0]?.label || 'Name (A-Z)'
}

function onDropdownToggle(show: boolean) {
  isDropdownOpen.value = show
}

function onSortOptionSelect(selection: MenuOption) {
  setSortOption(selection.value as SortOption)
}

function onNewDropdownSelect(selection: MenuOption) {
  setCurrentState(selection.value as AppCurrentState)
}

async function onRefresh() {
  switch (selectedSection.value) {
    case 'all':
      if (apiState.searchQuery && isLocalSearch.value) {
        // For local search, we need to refresh the data then apply the filter
        await Promise.all([apiState.folderItemsFetcher?.(apiState.currentFolderId), apiState.favoritesFetcher?.()])
        filterLocalItems(apiState.searchQuery, apiState.searchMetadataFilters)
      } else if (apiState.searchQuery) {
        // For global search, use fileListFetcher which handles the remote search
        await apiState.fileListFetcher?.()
      } else {
        // No search, just refresh folder items
        await Promise.all([apiState.folderItemsFetcher?.(apiState.currentFolderId), apiState.favoritesFetcher?.()])
      }
      break
    case 'favorites':
      await apiState.favoritesFetcher?.()
      break
    case 'recent':
      await apiState.recentFilesFetcher?.()
      break
  }
}

function handleSearch(newValue: string) {
  apiActions.setSearchQuery(newValue)
  if (selectedSection.value === 'all' && isLocalSearch.value) {
    if (newValue) {
      filterLocalItems(newValue, apiState.searchMetadataFilters)
    } else {
      apiActions.setFilteredItems([])
    }
  } else {
    apiState.fileListFetcher?.()
  }
}

function fetchFilesAfterFiltering(filters: Record<string, any>) {
  // Check if filters is empty or contains only empty values (empty arrays, null, undefined, etc.)
  const isEmpty =
    !filters ||
    Object.entries(filters).every(([, value]) => {
      if (Array.isArray(value)) return value.length === 0
      if (typeof value === 'object' && value !== null) return Object.keys(value).length === 0
      return value === null || value === undefined || value === ''
    })

  const normalizedFilters = isEmpty ? {} : filters
  apiActions.setSearchMetadataFilters(normalizedFilters)
  if (selectedSection.value === 'all' && isLocalSearch.value) {
    filterLocalItems(apiState.searchQuery, normalizedFilters)
  } else {
    apiState.fileListFetcher?.()
  }
}

function toggleSearchMode(isLocalSearch: boolean) {
  setIsLocalSearch(isLocalSearch)
  const hasSearchQuery = !!apiState.searchQuery
  const hasActiveFilters = Object.keys(apiState.searchMetadataFilters).length > 0

  if (hasSearchQuery || hasActiveFilters) {
    if (isLocalSearch) {
      filterLocalItems(apiState.searchQuery, apiState.searchMetadataFilters)
    } else {
      apiState.fileListFetcher?.()
    }
  }
}

function filterLocalItems(query: string, filters: Record<string, any>) {
  const normalizedQuery = query.toLowerCase()
  const folders = apiState.folders.filter((folder) => {
    return folder.name?.toLowerCase().includes(normalizedQuery)
  })
  const hasMetadataFilters = Object.keys(filters).length > 0

  const files = apiState.files.filter((file) => {
    const nameMatch = file.name?.toLowerCase().includes(normalizedQuery)
    const createdByMatch =
      file.created_by?.name?.toLowerCase().includes(normalizedQuery) ||
      file.created_by?.email?.toLowerCase().includes(normalizedQuery)

    const tagMatch = file.tags?.some((tag) => tag.toLowerCase().includes(normalizedQuery))

    // Filter by metadata properties if there are any filters
    let metadataMatch = true
    if (hasMetadataFilters) {
      // This is a simplified version - we'll handle metadata in a safer way
      for (const key in filters) {
        // We'll check if the key exists in the metadata and handle it safely
        const fileValue = (file as any).metadata?.[key]
        const filterValue = filters[key]
        if (
          filterValue &&
          (!fileValue || !String(fileValue).toLowerCase().includes(String(filterValue).toLowerCase()))
        ) {
          metadataMatch = false
          break
        }
      }
    }

    return (nameMatch || createdByMatch || tagMatch) && metadataMatch
  })

  // Process the results using 'as any' to bypass TypeScript issues

  const folderResults: any[] = folders.map((folder) => {
    const result: any = {
      id: folder.id,
      name: folder.name,
      folder_id: apiState.currentFolderId,
      folder: {
        id: apiState.currentFolderId,
        name: apiState.currentFolderName,
      },
      type: 'folder',
      file_thumbnail_urls: folder.file_thumbnail_urls,
      thumbnail_url: folder.thumbnail_url,
    }

    // Only add count properties if they exist
    if (folder.files_count !== undefined) result.files_count = folder.files_count
    if (folder.folders_count !== undefined) result.folders_count = folder.folders_count
    if (folder.available_files_count !== undefined) result.available_files_count = folder.available_files_count
    if (folder.available_folders_count !== undefined) result.available_folders_count = folder.available_folders_count

    return result
  })

  const fileResults: any[] = files.map((file) => {
    return {
      ...file,
      folder_id: apiState.currentFolderId,
      folder: {
        id: apiState.currentFolderId,
        name: apiState.currentFolderName,
      },
      type: 'file',
    }
  })

  apiActions.setFilteredItems(hasMetadataFilters ? [...fileResults] : [...folderResults, ...fileResults])
}

const popularityProps = computed(() => ({
  showLatestFilter: false,
}))

function enableSmartFolders() {
  smartFolderStore.isSmartFoldersView.value = true
}

const watcher = watch(
  () => apiState.searchQuery,
  (newValue, oldValue) => {
    if (searchFiltersRef.value && newValue.length === 0 && oldValue.length > 0) {
      searchFiltersRef.value.clearSearch()
    }
  },
)

onBeforeUnmount(() => {
  watcher.stop()
})
</script>

<style scoped lang="scss">
.breadcrumb-area {
  :deep(.n-button) {
    border: 1px solid v-bind('themeVars.primary4') !important;
  }
}

.content__breadcrumb__item.n-breadcrumb-item.n-breadcrumb-item {
  :deep(.n-breadcrumb-item__link) {
    font-size: 18px;
    line-height: 28px;
    letter-spacing: 0.25px;
  }
}

.content__section-title {
  font-size: 18px;
  line-height: 28px;
  letter-spacing: 0.25px;
  color: v-bind('themeVars.primary');
}
.actions-toolbar {
  background-color: v-bind('themeVars.primary6');

  &__nav {
    background: v-bind('themeVars.base');
  }

  &_filters {
    &-divider {
      margin-left: 0;
      margin-right: 8px;
      height: 36px;
    }
    &-clear {
      text-transform: none;
    }
  }
}
.actions-toolbar__controls {
  background: v-bind('themeVars.base');
}
</style>
